import { useMemo, useState } from "react";
import { Bell, BellOff, MoreHorizontal, Plus } from "lucide-react";
import { Button, EmptyState, Icon, MenuButton, Sheet, Spinner, useCoarsePointer, type MenuPlacement } from "@cntrl/ui";
import { useSetThreadMuted, useThreads, type ThreadSummary } from "../../app/hooks/useInbox.js";
import { useMe } from "../../app/hooks/useSession.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  threadTitle,
  threadTags,
  threadPreview,
  nameInitials,
  relativeTime,
  directCounterpartIds,
} from "../../app/hooks/inboxFormat.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { useLongPress } from "./useLongPress.js";
import { CrewMark } from "./CrewMark.js";
import styles from "./Inbox.module.scss";

export interface ThreadListProps {
  selectedThreadId?: string;
  onSelect: (threadId: string) => void;
  /**
   * Open the "new message" door. ONE door for both shapes (PO, 2026-09-11): the dialog behind it takes
   * several people, and how many you pick is what decides between a DM and a group chat — so the reader
   * never chooses the SHAPE of a conversation before choosing who is in it.
   */
  onNewThread: () => void;
  /**
   * COMPACT header (ART-186 — the mobile list page). Drops the "Messages" title + the "All caught up"
   * eyebrow (the shell header carries "Inbox" beside the logo now) and puts the search box and "+ New" on
   * one line. Desktop keeps the full title header.
   */
  compact?: boolean;
}

/**
 * The left-pane thread list: the "Messages" title + a black "+ New" pill, a "Search messages…" input, and
 * ONE flat list of chats. Each row = avatar + bold name + up to two tags + gray preview + a right-aligned
 * relative time + a crimson unread badge. Designed loading/error/empty states.
 *
 * **FLAT, and sorted by recency across every kind** (PO, 2026-09-11; the handoff's own "one flat, tab-less
 * list… a chat is identified by its name + tag, not by which tab it sits in"). It had a MESSAGES/DIRECT/SHOWS
 * tab row, then — when that went — DIRECT and SHOWS section headings, which is the same grouping wearing a
 * different hat: both answered "what kind of chat is this" by POSITION, and both buried the thing a reader
 * actually wants, which is what happened most recently. The TAGS answer that question now, per row, which is
 * what makes the flat list readable rather than merely shorter.
 *
 * **Nothing sorts here.** `GET /me/threads` orders on `last_message_on ?? created_on` and pages on the same
 * expression, so a client-side sort would be a second opinion that disagrees with the cursor at every page
 * boundary — the list would reorder as you scrolled.
 */
/**
 * The filter chips, in the order they render. `null` is "everything" and is always first — a filter row
 * with no way back is a trap, and the reader must be able to see the whole list in one press.
 *
 * They filter on the SERVED `tag` (and the absence of one), never on `kind`: the tag is the classification
 * the server owns, and a second derivation here would disagree the day a kind is added — which bus chats
 * (ART-160) will be. "Direct" is the untagged bucket rather than `kind === "direct"` for the same reason,
 * which also folds group chats in with DMs: both are conversations somebody started, and neither has a date
 * behind it.
 */
/** Below this many chats the filter row is chrome — the list it exists for is the twenty-chat one. */
const FILTER_THRESHOLD = 8;

/** Space a row's `…` menu needs below its trigger before it opens upward instead (one item plus the panel's
 *  padding and offset, with some margin). */
const MENU_ROOM_PX = 96;

const FILTERS: readonly { key: string | null; label: string }[] = [
  { key: null, label: "All" },
  { key: "show", label: "Shows" },
  { key: "tour", label: "Tours" },
  { key: "none", label: "Direct" },
];

export function ThreadList({ selectedThreadId, onSelect, onNewThread, compact }: ThreadListProps) {
  const [search, setSearch] = useState("");
  // C-Filter. The handoff flags this against its own flat list: "a Pro band on a 20-date run with 5 parties
  // and 3 buses lands ~10 chats in one flat list, sorted only by recency". CLIENT-side over the loaded
  // page, deliberately — `?kind=` exists on the route but filters by thread KIND, which is a different
  // vocabulary from the tag, and a server filter would page independently per chip and make the cursor a
  // per-filter fact. The row set is a page either way.
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  // Every thread, always. The MESSAGES/DIRECT/SHOWS filter row above the search is GONE (PO, 2026-09-11):
  // the list is already grouped into those same sections, so the tabs were a second rendering of one datum —
  // and the only one of the three that could hide anything was showing you a subset of a list you can see.
  const q = useThreads();
  const me = useMe();

  // The page's own person directory — the ONE place these rows get a name, bounded to the ids they
  // disclose. A DM is named for the other person in it; without this every one reads "Direct message".
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of q.data?.people ?? []) if (p.personId && p.displayName) map.set(p.personId, p.displayName);
    return map;
  }, [q.data]);

  // personId → avatarMediaId, off the SAME directory the names come from. `avatarMediaId` is the
  // existence/cache-bust signal every person-carrying DTO exposes; the bytes come from the visibility-gated
  // person route, which PersonAvatar owns — never `avatarUrl`, which is a slot nothing fills.
  const avatars = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const p of q.data?.people ?? []) if (p.personId) map.set(p.personId, p.avatarMediaId ?? null);
    return map;
  }, [q.data]);
  const titleCtx = useMemo(
    () => ({ names, myPersonId: me.data?.person?.personId }),
    [names, me.data],
  );
  const rowCtx = useMemo(() => ({ ...titleCtx, avatars }), [titleCtx, avatars]);

  // CHATS with something unread in them — the same unit the envelope's badge counts, read off the rows
  // rather than from a second query. It deliberately ignores the tag FILTER: the eyebrow is about the
  // inbox, not about the slice of it currently on screen, and a count that fell when you pressed "Shows"
  // would read as messages disappearing.
  const unreadChats = useMemo(
    () => (q.data?.threads ?? []).filter((t) => (t.unread ?? 0) > 0).length,
    [q.data],
  );

  const threads = useMemo(() => {
    const all = q.data?.threads ?? [];
    const tagged =
      tagFilter === null
        ? all
        // "none" is the ABSENCE of a tag, which is what makes the chip list total: every row matches
        // exactly one chip, so nothing can be invisible under every filter.
        : all.filter((t) => (t.tag ?? "none") === tagFilter);
    const needle = search.trim().toLowerCase();
    if (!needle) return tagged;
    // Matched against what the row SHOWS, not the raw body — otherwise searching for a name that only
    // appears as the preview's "Ryan: " prefix finds nothing, and a file name is unsearchable entirely.
    return tagged.filter(
      (t) =>
        threadTitle(t, titleCtx).toLowerCase().includes(needle) ||
        threadPreview(t, titleCtx).toLowerCase().includes(needle),
    );
  }, [q.data, search, titleCtx, tagFilter]);

  const searchInput = (
    <input
      className={styles.searchInput}
      type="search"
      placeholder="Search messages…"
      aria-label="Search messages"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />
  );
  const newButton = (
    <button type="button" className={styles.newBtn} onClick={onNewThread}>
      <Icon icon={Plus} size={16} /> New
    </button>
  );

  return (
    <>
      {compact ? (
        // ART-186 (mobile): no title, no eyebrow — the shell header shows "Inbox" beside the logo — and the
        // search box shares its line with "+ New".
        <div className={styles.compactHeader}>
          {searchInput}
          {newButton}
        </div>
      ) : (
        <>
          <div className={styles.listHeader}>
            <div className={styles.titleBlock}>
              <h1 className={styles.title}>Messages</h1>
              {/* THE EYEBROW (ART-159, C-Mobile) — "3 unread" / "All caught up". Derived from the ROWS this
                  list is showing rather than from the envelope's badge query, so the sentence and the list
                  under it can never disagree: a badge read from a second source would go stale against a list
                  that just repainted. It also counts CHATS, which is what the badge counts and what the list
                  contains — the C-Badge unit, one surface over.

                  Only once the list has landed: "All caught up" over a spinner is a claim about nothing. */}
              {!q.isLoading && !q.isError && (
                <p className={styles.eyebrow}>
                  {unreadChats > 0 ? `${unreadChats} unread` : "All caught up"}
                </p>
              )}
            </div>
            {newButton}
          </div>

          <div className={styles.search}>{searchInput}</div>
        </>
      )}

      {/* THE FILTER CHIPS (C-Filter). They render only when there is something to filter — a single chip
          row over four chats is chrome, and the list this exists for is the twenty-chat one. The threshold
          reads the UNFILTERED page, or choosing a narrow filter would hide the control that undoes it. */}
      {(q.data?.threads?.length ?? 0) > FILTER_THRESHOLD && (
        <div className={styles.filters} role="group" aria-label="Filter chats">
          {FILTERS.map((f) => (
            <button
              key={f.key ?? "all"}
              type="button"
              className={styles.filterChip}
              // `aria-pressed` rather than a `data-active` class alone: these are toggles, and a screen
              // reader has to be told which one is on.
              aria-pressed={tagFilter === f.key}
              data-active={tagFilter === f.key || undefined}
              onClick={() => setTagFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      <div className={styles.sections}>
        {q.isLoading ? (
          <div className={styles.stateCenter} role="status" aria-live="polite">
            <Spinner size={20} label="Loading messages…" />
          </div>
        ) : q.isError ? (
          <div className={styles.stateCenter}>
            <EmptyState
              title="Couldn't load messages"
              description={errorMessage(q.error, "Please try again.")}
              action={<Button size="sm" onClick={() => void q.refetch()}>Retry</Button>}
            />
          </div>
        ) : threads.length === 0 ? (
          <div className={styles.stateCenter}>
            <EmptyState
              title={search || tagFilter ? "No matches" : "No messages yet"}
              // A description ONLY when there is something to say: "try a different search" is an action,
              // where pointing at the button sitting six inches above it was the title in other words.
              description={
                search ? "Try a different search." : tagFilter ? "No chats of this kind." : undefined
              }
            />
          </div>
        ) : (
          threads.map((t) => (
            <ThreadRow
              key={t.threadId}
              thread={t}
              active={t.threadId === selectedThreadId}
              titleCtx={rowCtx}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </>
  );
}

function ThreadRow({
  thread,
  active,
  titleCtx,
  onSelect,
}: {
  thread: ThreadSummary;
  active: boolean;
  /** The page's person directory + the viewer, so a DM row can NAME and PICTURE the other person. */
  titleCtx: {
    names: ReadonlyMap<string, string>;
    avatars: ReadonlyMap<string, string | null>;
    myPersonId?: string;
  };
  onSelect: (threadId: string) => void;
}) {
  const name = threadTitle(thread, titleCtx);
  const tags = threadTags(thread);
  const preview = threadPreview(thread, titleCtx);
  // The SAME derivation the title uses, so the face and the name are always the same person. Only a DM has
  // one: a show chat is a crew.
  const dmPersonId =
    thread.kind === "direct" ? directCounterpartIds(thread, titleCtx.myPersonId)[0] : undefined;
  // The thread you are LOOKING AT is never unread. The server agrees a beat later — ThreadView re-marks on
  // every arriving message — but the list is invalidated by the same signal that delivered it, so without
  // this the badge paints for the round trip and then clears itself. A count that appears on the
  // conversation you are already reading, then vanishes on its own, is worse than no count.
  const unread = active ? 0 : thread.unread ?? 0;
  // The chat's own picture first, then the show's flyer — the SAME fallback chain the header resolves, so a
  // chat somebody re-photoed looks like itself in the sidebar and in its own head rather than only in one.
  // The chat's own picture (global avatar store — no band in the path) else the show's FLYER, which is
  // genuinely that band's file. The SAME fallback chain the header resolves, so a chat somebody re-photoed
  // looks like itself in the sidebar and in its own head rather than only in one.
  const chatPicture = useAvatarUrl("thread", thread.threadId, thread.avatarMediaId);
  const flyer = useMediaObjectUrl(
    chatPicture ? null : (thread.show?.flyerMediaId ?? null),
    null,
    { bandId: thread.show?.bandId, variant: "thumb" },
  );
  const markUrl = chatPicture ?? flyer.url;

  // THE ROW'S OWN ACTIONS (ART-270) — today just mute, which moved here from the chat's Members dialog so it can
  // be set without opening the chat. A finger HOLDS the row for a sheet; a mouse gets a `…` on hover. Chosen by
  // INPUT, not width, for the reason `useCoarsePointer` gives: a narrow desktop window still has a mouse.
  const coarsePointer = useCoarsePointer();
  const [sheetOpen, setSheetOpen] = useState(false);
  const hold = useLongPress(() => setSheetOpen(true), coarsePointer);
  const setMuted = useSetThreadMuted();
  const muted = thread.muted ?? false;
  const toggleMute = () => thread.threadId && setMuted.mutate({ threadId: thread.threadId, muted: !muted });
  const muteLabel = muted ? "Unmute notifications" : "Mute notifications";

  // Which way the `…` menu opens. Below by default; ABOVE when the row sits too near the bottom of the scrolling
  // list for the menu to fit, or it would hang past the scroller's edge and be clipped. Measured on the press that
  // opens it (pointer or key), because the row's place in the list changes as the list scrolls.
  const [menuPlacement, setMenuPlacement] = useState<MenuPlacement>("below");
  const placeMenu = (e: { currentTarget: HTMLElement }) => {
    const scroller = e.currentTarget.closest(`.${styles.sections}`);
    if (!scroller) return;
    const room = scroller.getBoundingClientRect().bottom - e.currentTarget.getBoundingClientRect().bottom;
    setMenuPlacement(room < MENU_ROOM_PX ? "above" : "below");
  };

  return (
    // A wrapper rather than the row button itself, because the `…` trigger is a BUTTON too and a button inside a
    // button is invalid markup. The hold handlers live here so the click that ends a hold is swallowed before it
    // reaches the row and opens the chat under the sheet.
    <div
      className={styles.threadRowWrap}
      data-active={active || undefined}
      data-hold={coarsePointer || undefined}
      {...hold}
    >
    <button
      type="button"
      className={active ? `${styles.threadRow} ${styles.threadRowActive}` : styles.threadRow}
      onClick={() => thread.threadId && onSelect(thread.threadId)}
    >
      {dmPersonId ? (
        // A DM shows the OTHER PERSON'S FACE. Through PersonAvatar, never a raw <img>: it owns the
        // authenticated, visibility-gated person fetch, the person→circle shape, and the owner-is-a-band
        // special case — and it is context-safe, which this row needs because the inbox also opens under the
        // roster shell where no band is mounted.
        <PersonAvatar
          personId={dmPersonId}
          avatarMediaId={titleCtx.avatars.get(dmPersonId) ?? null}
          initials={nameInitials(name)}
          size="md"
        />
      ) : thread.kind === "bus" && !chatPicture ? (
        // A BUS chat with no picture of its own (ART-304) is drawn as the people bunked on it: it has no show
        // and so no flyer, and "#" made every coach look like an unfinished row.
        <CrewMark
          personIds={directCounterpartIds(thread, titleCtx.myPersonId)}
          avatars={titleCtx.avatars}
          names={titleCtx.names}
          fallback="#"
        />
      ) : (
      <span className={styles.avatar} aria-hidden="true">
        {/* A show thread's FLYER, when the show has one — uploaded on the show editor's 01 Basics tile and
            written through the show PUT. Initials are the fallback for a show with no flyer, which is an
            ordinary state rather than the only one.
            `variant: "thumb"` degrades to the original until the worker generates the rendition, and the
            band id is required because media serves band-scoped while this pane has no band mounted. */}
        {/* The same picture-less GROUP mark the header draws (ThreadView's GROUP_MARK). Initials are for
            PEOPLE: a show chat's name reduces to nonsense — "Bluebird [test data] · Oct 1" gives "B[" —
            because a venue-plus-date is not a name with initials in it. */}
        {markUrl ? <img className={styles.avatarImg} src={markUrl} alt="" /> : "#"}
      </span>
      )}
      <span className={styles.rowBody}>
        <span className={styles.rowTop}>
          <span className={styles.name}>{name}</span>
          {/* MUTED (ART-270): the chat still lists and counts unread — only its pushes are off — so the row says
              so, or a muted chat and a quiet one look the same and the mute is forgotten. */}
          {muted && (
            <span className={styles.mutedMark} role="img" aria-label="Muted">
              <Icon icon={BellOff} size={16} />
            </span>
          )}
          {/* UP TO TWO TAGS (PO, 2026-09-11): what kind of chat, and which party. They carry what the name
              used to — the name is the venue now, so a flat list of "Bluebird" rows has nothing else to
              tell them apart, which is exactly the job a tag is for. */}
          {(tags.kind || tags.party) && (
            <span className={styles.rowTags}>
              {tags.kind && (
                <span className={styles.tag} data-tone={tags.kindTone}>
                  {tags.kind}
                </span>
              )}
              {tags.party && (
                <span className={styles.tag} data-tone="party">
                  {tags.party}
                </span>
              )}
            </span>
          )}
        </span>
        {/* ONE preview rule, because mobile renders this row too: the sender prefix on a multi-person chat
            (C-Prefix), and "📷 Photo" / "📎 name" when the last message was a file with no words
            (C-Attach). Never `lastPreview` raw — that is the BODY, and a picture-only message has none. */}
        {preview && <span className={styles.preview}>{preview}</span>}
      </span>
      <span className={styles.rowMeta}>
        <span className={styles.time}>{relativeTime(thread.lastMessageOn)}</span>
        {unread > 0 && (
          <span className={styles.unread} aria-label={`${unread} unread`}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </span>
    </button>
    {!coarsePointer && (
      <div className={styles.rowMenu} onPointerDownCapture={placeMenu} onKeyDownCapture={placeMenu}>
      <MenuButton
        menuLabel={`More actions for ${name}`}
        size="sm"
        placement={menuPlacement}
        items={[{ label: muteLabel, icon: muted ? Bell : BellOff, onSelect: toggleMute }]}
      >
        <Icon icon={MoreHorizontal} size={16} />
      </MenuButton>
      </div>
    )}
    {sheetOpen && (
      <Sheet title={name} onClose={() => setSheetOpen(false)} closeLabel="Close" backdropClassName={styles.actionsSheetBackdrop}>
        <div className={styles.actionsSheetList}>
          <button
            type="button"
            className={styles.actionsSheetItem}
            onClick={() => {
              setSheetOpen(false);
              toggleMute();
            }}
          >
            <Icon icon={muted ? Bell : BellOff} size={20} />
            {muteLabel}
          </button>
        </div>
      </Sheet>
    )}
    </div>
  );
}
